import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import useModalClose from 'hooks/useModalClose'
import { CursorPointer, Flex, ModalScrollContainer } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IGetAllInventoryAssignmentsSingleResponse } from 'types/api/inventoryItems.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { formatDateForDisplay } from 'utils/helpers'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { inventoryAssignmentStatuses } from './InventoryAssignments'
import {
    AssignmentDetailGrid,
    AssignmentDetailItem,
    AssignmentDetailValue,
    AssignmentModalBody,
    AssignmentSerialsText,
} from './ViewInventoryAssignmentModal.styled'

const ASSIGNMENT_STATUS_TONES: { [status: number]: IStatusPill['status'] } = {
    1: 'warning',
    2: 'success',
    3: 'danger',
}

const assignmentLineColumns: IHonestTableColumn<IGetAllInventoryAssignmentsSingleResponse['lines'][number]>[] = [
    {
        id: 'item',
        header: 'ITEM',
        fill: true,
        cell: (line) =>
            line.inventoryItem?.name ? (
                <BodyText>{line.inventoryItem.name}</BodyText>
            ) : (
                <OtherText grey>—</OtherText>
            ),
    },
    {
        id: 'sku',
        header: 'SKU',
        cell: (line) =>
            line.inventoryItem?.sku ? (
                <BodyText>{line.inventoryItem.sku}</BodyText>
            ) : (
                <OtherText grey>—</OtherText>
            ),
    },
    {
        id: 'quantity',
        header: 'QTY',
        align: 'right',
        cell: (line) => <BodyText>{line.quantity}</BodyText>,
    },
    {
        id: 'serials',
        header: 'SERIALS',
        cell: (line) =>
            line.serials?.length ? (
                <AssignmentSerialsText>
                    {line.serials.map((serial) => `#${serial.serialId}`).join(', ')}
                </AssignmentSerialsText>
            ) : (
                <OtherText grey>—</OtherText>
            ),
    },
]

export const ViewInventoryAssignmentModal = ({ closeModal, value }: { closeModal: any, value?: IGetAllInventoryAssignmentsSingleResponse }) => {
    useFocusFirstInput()
    useModalClose(closeModal)

    return <Modal black>
        <AssignmentModalBody>
            <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
                <Flex center gap='0.75rem'>
                    <TitleText weight='bold' >{`Assignment #${value?.id ?? '—'}`}</TitleText>
                    {value?.status != null && (
                        <StatusPill status={ASSIGNMENT_STATUS_TONES[value.status] || 'neutral'}>
                            {inventoryAssignmentStatuses.find(s => s.value == value?.status)?.label || '—'}
                        </StatusPill>
                    )}
                </Flex>
                <CursorPointer onClick={closeModal} >
                    <CloseIcon />
                </CursorPointer>
            </Flex>
            <ModalScrollContainer>
                <AssignmentDetailGrid>
                    <AssignmentDetailItem>
                        <OtherText grey>CREATED</OtherText>
                        <AssignmentDetailValue>
                            {value?.createdAt ? formatDateForDisplay(value.createdAt) : '—'}
                        </AssignmentDetailValue>
                    </AssignmentDetailItem>
                    <AssignmentDetailItem>
                        <OtherText grey>TYPE</OtherText>
                        <AssignmentDetailValue>{value?.type == 1 ? 'Assign' : 'Unassign'}</AssignmentDetailValue>
                    </AssignmentDetailItem>
                    <AssignmentDetailItem>
                        <OtherText grey>LOCATION</OtherText>
                        <AssignmentDetailValue>{value?.location?.name || '—'}</AssignmentDetailValue>
                    </AssignmentDetailItem>
                    <AssignmentDetailItem>
                        <OtherText grey>ASSIGNEE</OtherText>
                        <AssignmentDetailValue>—</AssignmentDetailValue>
                    </AssignmentDetailItem>
                    <AssignmentDetailItem>
                        <OtherText grey>LINES</OtherText>
                        <AssignmentDetailValue>{value?.lines?.length ?? 0}</AssignmentDetailValue>
                    </AssignmentDetailItem>
                    <AssignmentDetailItem>
                        <OtherText grey>UNITS</OtherText>
                        <AssignmentDetailValue>
                            {(value?.lines || []).reduce((sum, line) => sum + (line.quantity || 0), 0)}
                        </AssignmentDetailValue>
                    </AssignmentDetailItem>
                </AssignmentDetailGrid>

                <Flex column gap='0.25rem' mb='16'>
                    <OtherText grey>NOTES</OtherText>
                    {value?.notes ? (
                        <BodyText>{value.notes}</BodyText>
                    ) : (
                        <OtherText grey>—</OtherText>
                    )}
                </Flex>

                <Flex column gap='0.5rem'>
                    <OtherText grey>ITEMS</OtherText>
                    {value?.lines?.length ? (
                        <HonestTable columns={assignmentLineColumns} data={value.lines} keyField='id' />
                    ) : (
                        <OtherText grey>No items on this assignment.</OtherText>
                    )}
                </Flex>
            </ModalScrollContainer>
        </AssignmentModalBody>
    </Modal>
}
